import { createContext, useContext, useMemo, type ReactNode } from "react"
import { useNavigate } from "react-router"
import { useQueryClient } from "@tanstack/react-query"

import { useCurrentUser, useLogout } from "@/api/hooks/auth"
import type { User } from "@/api/client"
import { userHasNetwork, userNetworkSlugs } from "@/lib/networkAccess"

interface AuthContextValue {
  user: User | null
  isLoading: boolean
  isAdmin: boolean
  isTeamLeader: boolean
  canUseArticleGenerator: boolean
  networkSlugs: string[]
  hasNetwork: (slug: string) => boolean
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Fetches `/auth/me` on mount and exposes `{user, isLoading, isAdmin,
 * isTeamLeader, canUseArticleGenerator, logout}` to descendants. Uses
 * cookie-mode auth (T1.3) — the httpOnly cookie is attached automatically
 * via `credentials: "include"` in `apiFetch`, so no token juggling in the
 * browser.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: user, isLoading } = useCurrentUser()
  const logoutMutation = useLogout()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const value = useMemo<AuthContextValue>(
    () => ({
      user: user ?? null,
      isLoading,
      isAdmin: user?.role === "admin",
      isTeamLeader: user?.role === "team_leader",
      canUseArticleGenerator:
        user?.role === "admin" ||
        user?.role === "team_leader" ||
        user?.role === "mediabuyer",
      networkSlugs: userNetworkSlugs(user),
      hasNetwork: (slug: string) => userHasNetwork(user, slug),
      logout: () => {
        logoutMutation.mutate(undefined, {
          onSettled: () => {
            // Drop every cached query so a future re-login on the same
            // tab doesn't bleed the previous user's data, then client-
            // side navigate to /login (no full reload — preserves
            // router state and avoids the cache cost of a fresh boot).
            queryClient.clear()
            navigate("/login", { replace: true })
          },
        })
      },
    }),
    [user, isLoading, logoutMutation, navigate, queryClient],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider/>")
  }
  return ctx
}
